import { RECONCILIATION_THRESHOLD_UNITS } from '@trames/shared/constants';
import { RealtimeEventTag, type GameSnapshotPayload } from '@trames/shared/schemas';

import type { EntitySample } from '../render/interpolation.js';
import type { Room } from 'colyseus.js';

/**
 * Detector de divergencia cliente↔servidor (TKT-018).
 *
 * Sin anticipación cliente, la divergencia esperada en localhost es ~0. La métrica
 * anticipa bugs futuros: si cambiamos la fuente del render (predicción, lag-comp),
 * verán correcciones >0 y sabremos pronto.
 *
 * Diseño:
 *   - El cliente expone su última posición rendereada por entidad (TKT-015).
 *   - Comparamos con el último snapshot autoritativo (mismo entityId).
 *   - Si |delta| > RECONCILIATION_THRESHOLD_UNITS, emitimos un report al server.
 *   - Throttle: como máximo 1 report por entidad por segundo, para no saturar.
 */

const REPORT_INTERVAL_MS = 1000;

export interface ReconciliationOptions {
  /** Recupera la última muestra rendereada por entityId. */
  getRendered: (entityId: string) => EntitySample | undefined;
  /** Sumidero para reportes (en spike 2: room.send). */
  send: (payload: { seatId: string; deltaUnits: number; serverTickId: number }) => void;
  /** Inyectable para tests. */
  now?: () => number;
  /** Umbral en unidades de mundo. */
  thresholdUnits?: number;
}

export interface ReconciliationStats {
  correctionsBySeat: Map<string, number>;
}

export function createReconciliationProbe(opts: ReconciliationOptions): {
  inspect: (snap: GameSnapshotPayload) => ReconciliationStats;
  stats: ReconciliationStats;
} {
  const lastReportAt = new Map<string, number>();
  const stats: ReconciliationStats = { correctionsBySeat: new Map() };
  const now = opts.now ?? Date.now;
  const threshold = opts.thresholdUnits ?? RECONCILIATION_THRESHOLD_UNITS;

  return {
    inspect: (snap) => {
      for (const entity of snap.entities) {
        const rendered = opts.getRendered(entity.entityId);
        if (!rendered) continue;
        const dx = entity.position.x - rendered.position.x;
        const dy = entity.position.y - rendered.position.y;
        const delta = Math.hypot(dx, dy);
        if (delta <= threshold) continue;
        const t = now();
        const lastT = lastReportAt.get(entity.entityId) ?? Number.NEGATIVE_INFINITY;
        if (t - lastT < REPORT_INTERVAL_MS) continue;
        lastReportAt.set(entity.entityId, t);
        stats.correctionsBySeat.set(
          entity.entityId,
          (stats.correctionsBySeat.get(entity.entityId) ?? 0) + 1,
        );
        opts.send({ seatId: entity.entityId, deltaUnits: delta, serverTickId: snap.tickId });
      }
      return stats;
    },
    stats,
  };
}

/**
 * Helper para conectar la probe al envío WS via Room.send.
 * `getRendered` viene del GameScene del cliente.
 */
export function createRoomReconciler(
  room: Room,
  getRendered: (entityId: string) => EntitySample | undefined,
): (snap: GameSnapshotPayload) => ReconciliationStats {
  const probe = createReconciliationProbe({
    getRendered,
    send: (payload) => {
      room.send(RealtimeEventTag.ReconciliationReport, {
        t: RealtimeEventTag.ReconciliationReport,
        p: payload,
        ts: Date.now(),
      });
    },
  });
  return probe.inspect;
}
